import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import { TextP } from '../../Defaults/Text';
import {
  FlexColCenter,
  FlexCol,
  FlexRowCenterVertical,
  FlexRowCenter,
} from '../../Defaults/Flexbox/Flexbox';
import { ButtonWithRequest } from './ButtonWithRequest';
import { animateIn } from './ProductTabDefault';
import {
  getProductDefaultInfo,
  getProductSmallInfo,
} from '../../../utilities/selectors';
import { getZemplinProductDefaultInfo } from '../selectors';
import { __ } from 'react-i18n';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { round } from '../../../utilities';

export interface Props {
  product: any;
  addToRequest: any;
  addToCart: any;
  maxWidth?: number;
  user?: any;
  onClick?: any;
  isInCart?: boolean;
  theme: any;
}

function ProductTabSmall({
  product,
  addToCart,
  addToRequest,
  maxWidth = 333,
  user,
  onClick,
  isInCart,
  theme,
}: Props) {
  const { url, name, currency } = getProductDefaultInfo(product);
  const { picture } = getProductSmallInfo(product);
  const {
    unit,
    price,
    oldPrice,
    priceWithoutVat,
    onStock,
    statusB2B,
    statusB2C,
    isSpojMat,
    specialDiscount,
    specialDiscountPcn,
    specialDiscountWithoutVat,
  } = getZemplinProductDefaultInfo(product);
  const isB2b = user && user.b2b;
  const statusShowPrice = isB2b ? statusB2B !== 3 : statusB2C !== 3;
  const statusShowAddToCart = isB2b ? statusB2B === 0 : statusB2C === 0;
  const correctPrice = statusShowPrice
    ? isB2b
      ? (+priceWithoutVat).toFixed(2)
      : (+price).toFixed(2)
    : '';
  const totalPriceText = isB2b ? __('bez DPH') : __('s DPH');
  const noStock = !statusShowAddToCart || (!isB2b ? +onStock === 0 : false);

  const correctShownSpecialDiscount =
    specialDiscountPcn && specialDiscountPcn !== 0
      ? specialDiscountPcn
      : isB2b
      ? specialDiscountWithoutVat
      : specialDiscount;
  const correctShownSpecialDiscountText =
    correctShownSpecialDiscount && correctShownSpecialDiscount !== 0
      ? specialDiscountPcn && specialDiscountPcn
        ? `-${correctShownSpecialDiscount}%`
        : `${correctShownSpecialDiscount}${currency}`
      : '';

  let oldPriceCorrect =
    onStock > 0 || !isSpojMat ? (isB2b ? +priceWithoutVat : +price) : 0;
  oldPriceCorrect = correctShownSpecialDiscountText.includes('%')
    ? oldPriceCorrect / ((100 - correctShownSpecialDiscount) / 100)
    : correctShownSpecialDiscountText.includes(currency)
    ? oldPriceCorrect - +correctShownSpecialDiscount
    : 0;

  return (
    <Wrapper maxWidth={maxWidth}>
      <TopWrapper onClick={onClick} to={url}>
        <FlexRowCenterVertical
          style={{ width: '100%', maxWidth: rem(80), marginRight: rem(24) }}
        >
          <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
          {isInCart && (
            <SvgIcon
              width={16}
              height={16}
              icon={IconType.cart}
              fill={theme.colors.primary}
              marginLeft={8}
              alt={__('Produkt sa nachádza v košíku')}
              title={__('Produkt sa nachádza v košíku')}
            />
          )}
        </FlexRowCenterVertical>
        <Name>{name}</Name>
      </TopWrapper>
      <BottomWrapper>
        <PriceCol>
          <PriceRow>
            <Price>{`${correctPrice} ${currency}`}</Price>
            <Unit>{`/ ${unit}`}</Unit>
          </PriceRow>
          <SecondaryText>{totalPriceText}</SecondaryText>
        </PriceCol>
        <FlexColCenter>
          {oldPriceCorrect ? (
            <OldPrice>{`${round(
              oldPriceCorrect,
              2,
            )} ${currency} / ${unit}`}</OldPrice>
          ) : null}
          <ButtonWithRequest
            onButtonClick={addToCart}
            onRequestClick={addToRequest}
            noStock={noStock}
            isInCart={isInCart}
          />
        </FlexColCenter>
      </BottomWrapper>
    </Wrapper>
  );
}

const Wrapper = styled(FlexColCenter)<{ maxWidth: number }>`
  animation: ${animateIn} 0.3s ease;
  width: 100%;
  min-width: ${rem(250)};
  max-width: ${({ maxWidth }) => rem(maxWidth)};
  padding: ${rem(16)} ${rem(24)} ${rem(24)};
  border-radius: ${rem(2)};
  border: ${({ theme }) => theme.borders.product};
  margin-right: ${rem(16)};
  background-color: ${({ theme }) => theme.colors.white};

  &:last-child {
    margin-right: 8px;
  }

  ${({ theme }) => theme.mediab.l925`
    width: 95%;
    max-width: initial;
    max-width: ${({ maxWidth }) =>
      maxWidth === 333 ? 'initial' : rem(maxWidth)};
  `}

  ${({ theme }) => theme.mediab.m760`
    margin: 0 auto;
    max-width: ${({ maxWidth }) =>
      maxWidth === 333 ? 'initial' : rem(maxWidth)};
    width: 100%;
    &:last-child {
      margin: 0 auto;
    }
  `}
`;

const TopWrapper = styled(Link)`
  display: flex;
  flex-flow: row;
  align-items: center;
  margin-bottom: ${rem(24)};
  width: 100%;
`;

const BottomWrapper = styled(FlexRowCenter)`
  width: 100%;
  justify-content: space-between;
`;

const Image = styled.img`
  width: ${rem(80)};
  height: ${rem(80)};
  /* margin-right: ${rem(24)}; */
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(0.9);
  }

`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.primary};

  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  display: -webkit-box;
`;

const PriceCol = styled(FlexCol)``;

const PriceRow = styled(FlexRowCenterVertical)`
  margin-bottom: ${rem(4)};
`;

const Price = styled(TextP)`
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(20)};
  line-height: ${rem(22)};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-right: ${rem(6)};
  white-space: nowrap;
`;

const Unit = styled(TextP)`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(22)};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-right: ${rem(16)};
  white-space: nowrap;
`;

const SecondaryText = styled(TextP)`
  font-family: ${({ theme }) => theme.font.tertiary};
  font-size: ${rem(12)};
  line-height: ${rem(20)};
  color: ${({ theme }) => theme.colors.placeholder};
`;

const OldPrice = styled(SecondaryText)`
  text-decoration: line-through;
  white-space: nowrap;

  ${({ theme }) => theme.mediab.l1050`
    display: none;
  `}
`;

const WithTheme: any = withTheme(ProductTabSmall);
export { WithTheme as ProductTabSmall };
